/**
 * Campos do cadastro de caso — fonte única (E56.F3.T01).
 *
 * Os mesmos nove campos existiam escritos duas vezes: em `NovoCasoPage` (criar)
 * e em `CaseEditStep` (editar dentro do wizard). Era a raiz da redundância que o
 * owner apontou — duas telas com o mesmo formulário, e qualquer mudança de
 * cadastro tendo de ser feita nos dois lugares sob risco de divergirem.
 *
 * O componente é controlado e sem casca: quem monta decide o `<form>`, os botões
 * e o agrupamento. `prefix` gera `id` e `data-testid` (`novo-caso-*` na criação,
 * `wizard-case-*` no painel do wizard), preservando os seletores que a suíte e
 * os smokes já usam.
 */
import type { ChangeEvent } from 'react';

import { DateShortcuts } from '@/components/NovoCaso/DateShortcuts';
import { CasoField } from '@/components/NovoCaso/NovoCasoFormField';
import { Input, Radio, Select, Textarea } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import { formatCnj, formatCpf } from '@/lib/masks';
import {
  ESPECIME_OPTIONS,
  MODALIDADE_OPTIONS,
  type FieldErrors,
  type FormState,
} from '@/pages/novo_caso_form';

/** Grupo de campos a renderizar. */
export type CaseFieldsGrupo = 'essenciais' | 'detalhes';

export interface CaseFieldsFieldsetProps {
  /** Prefixo de `id`/`data-testid` — `novo-caso` ou `wizard-case`. */
  prefix: string;
  grupo: CaseFieldsGrupo;
  state: FormState;
  errors: FieldErrors;
  disabled: boolean;
  onChange: <K extends keyof FormState>(key: K, value: FormState[K]) => void;
  /**
   * E74.F1 — a modalidade é escolhida na criação e não muda (ADR-0075 §1): o
   * passo "Caso" do wizard (caso já existente) mostra o campo travado.
   */
  modalidadeBloqueada?: boolean;
}

/**
 * Seletor de modalidade (E74.F1). Radios nativos dentro de `<fieldset>`: a
 * escolha é exclusiva e o rótulo do grupo viaja pela `<legend>`, sem depender
 * do contexto do `Field` (que associa UM controle ao rótulo).
 */
function CampoModalidade(props: CaseFieldsFieldsetProps) {
  const { prefix, state, disabled, onChange, modalidadeBloqueada = false } = props;
  const travado = disabled || modalidadeBloqueada;
  const hintId = `${prefix}-modalidade-hint`;
  return (
    <fieldset
      className="flex flex-col sm:col-span-2"
      data-testid={`${prefix}-modalidade`}
      aria-describedby={hintId}
    >
      <legend className="mb-[5px] block text-caption text-fg-dim">
        {ptBR.casos.fieldModalidade}
      </legend>
      <div className="flex flex-wrap gap-x-6 gap-y-2">
        {MODALIDADE_OPTIONS.map((opt) => (
          <Radio
            key={opt.value}
            name={`${prefix}-modalidade`}
            value={opt.value}
            checked={state.modalidade === opt.value}
            disabled={travado}
            data-testid={`${prefix}-modalidade-${opt.value}`}
            onChange={() => onChange('modalidade', opt.value)}
          >
            {opt.label}
          </Radio>
        ))}
      </div>
      <p id={hintId} className="mt-1 text-caption text-fg-dim">
        {modalidadeBloqueada
          ? ptBR.casos.fieldModalidadeBloqueadaHint
          : ptBR.casos.fieldModalidadeHint}
      </p>
    </fieldset>
  );
}

/**
 * Essenciais: o que o backend exige para o caso existir (título e data de
 * recebimento). Tudo o mais é detalhe do dossiê e pode ser preenchido depois —
 * inclusive porque boa parte vem preenchida da CCB importada.
 */
function CamposEssenciais(props: CaseFieldsFieldsetProps) {
  const { prefix, state, errors, disabled, onChange } = props;
  return (
    <>
      <CasoField
        id={`${prefix}-title`}
        label={ptBR.casos.fieldTitle}
        required
        error={errors.title}
        colSpan="full"
      >
        <Input
          id={`${prefix}-title`}
          data-testid={`${prefix}-titulo`}
          value={state.title}
          disabled={disabled}
          onChange={(e: ChangeEvent<HTMLInputElement>) => onChange('title', e.target.value)}
        />
      </CasoField>

      <CampoModalidade {...props} />

      <CasoField
        id={`${prefix}-received`}
        label={ptBR.casos.fieldReceivedAt}
        required
        error={errors.receivedAt}
      >
        <Input
          id={`${prefix}-received`}
          data-testid={`${prefix}-recebido-em`}
          type="date"
          value={state.receivedAt}
          disabled={disabled}
          onChange={(e: ChangeEvent<HTMLInputElement>) => onChange('receivedAt', e.target.value)}
          className="font-mono"
        />
      </CasoField>

      <CasoField id={`${prefix}-deadline`} label={ptBR.casos.fieldDeadline} error={errors.deadline}>
        <Input
          id={`${prefix}-deadline`}
          data-testid={`${prefix}-prazo`}
          type="date"
          value={state.deadline}
          disabled={disabled}
          onChange={(e: ChangeEvent<HTMLInputElement>) => onChange('deadline', e.target.value)}
          className="font-mono"
        />
        <DateShortcuts
          testIdPrefix={`${prefix}-prazo`}
          disabled={disabled}
          onSet={(iso) => onChange('deadline', iso)}
          base={state.receivedAt}
          showClear={state.deadline !== ''}
          onClear={() => onChange('deadline', '')}
        />
      </CasoField>
    </>
  );
}

function CamposDetalhes(props: CaseFieldsFieldsetProps) {
  const { prefix, state, errors, disabled, onChange } = props;
  return (
    <>
      <CasoField id={`${prefix}-requester`} label={ptBR.casos.fieldRequester}>
        <Input
          id={`${prefix}-requester`}
          data-testid={`${prefix}-solicitante`}
          value={state.requester}
          disabled={disabled}
          onChange={(e: ChangeEvent<HTMLInputElement>) => onChange('requester', e.target.value)}
        />
      </CasoField>

      <CasoField
        id={`${prefix}-process`}
        label={ptBR.casos.fieldProcessNumber}
        error={errors.processNumber}
      >
        {/* CNJ é dado pericial: mono, como em toda a tabela de casos. */}
        <Input
          id={`${prefix}-process`}
          data-testid={`${prefix}-cnj`}
          value={state.processNumber}
          disabled={disabled}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            onChange('processNumber', formatCnj(e.target.value))
          }
          className="font-mono"
          placeholder={ptBR.casos.placeholderEmDash}
          inputMode="numeric"
          maxLength={25}
        />
      </CasoField>

      <CasoField id={`${prefix}-especime`} label={ptBR.casos.fieldEspecime}>
        <Select
          id={`${prefix}-especime`}
          data-testid={`${prefix}-especime`}
          value={state.especime}
          disabled={disabled}
          onChange={(e: ChangeEvent<HTMLSelectElement>) => onChange('especime', e.target.value)}
        >
          {ESPECIME_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </Select>
      </CasoField>

      <CasoField
        id={`${prefix}-cpf`}
        label={ptBR.casos.fieldCpf}
        hint={ptBR.casos.fieldCpfHint}
        error={errors.cpf}
      >
        <Input
          id={`${prefix}-cpf`}
          data-testid={`${prefix}-cpf`}
          value={state.cpf}
          disabled={disabled}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            onChange('cpf', formatCpf(e.target.value))
          }
          className="font-mono"
          placeholder="000.000.000-00"
          inputMode="numeric"
          maxLength={14}
        />
      </CasoField>

      <CasoField
        id={`${prefix}-contrato`}
        label={ptBR.casos.fieldContrato}
        hint={ptBR.casos.fieldContratoHint}
      >
        <Input
          id={`${prefix}-contrato`}
          data-testid={`${prefix}-contrato`}
          value={state.contrato}
          disabled={disabled}
          onChange={(e: ChangeEvent<HTMLInputElement>) => onChange('contrato', e.target.value)}
          className="font-mono"
        />
      </CasoField>

      <CasoField id={`${prefix}-parties`} label={ptBR.casos.fieldParties} colSpan="full">
        <Textarea
          id={`${prefix}-parties`}
          data-testid={`${prefix}-partes`}
          value={state.parties}
          disabled={disabled}
          onChange={(e: ChangeEvent<HTMLTextAreaElement>) => onChange('parties', e.target.value)}
          rows={2}
          className="min-h-[64px]"
        />
      </CasoField>
    </>
  );
}

export function CaseFieldsFieldset(props: CaseFieldsFieldsetProps): JSX.Element {
  return props.grupo === 'essenciais' ? (
    <CamposEssenciais {...props} />
  ) : (
    <CamposDetalhes {...props} />
  );
}
